'use no memo';

import * as React from 'react';

import {
  onlineManager,
  QueryObserverBaseResult,
  UseQueryResult,
} from '@tanstack/react-query';

import { ConnectionErrorFallback } from '../../ui/feedback/ConnectionErrorFallback';
import { Loading } from '../../ui/feedback/Loading';
import {
  createDataFetchError,
  isDataFetchError,
  toQueryHttpError,
} from '../../utilities/errors/DataFetchError';
import {
  getErrorDiagnostics,
  logErrorDiagnosticsToConsole,
} from '../../utilities/errors/getErrorDiagnostics';
import {
  ErrorResponse,
  hasErrorResponse,
} from '../../utilities/errors/hasErrorResponse';
import { HttpError } from '../../utilities/errors/HTTPErrors';
import { isCancellationError } from '../../utilities/errors/isCancellationError';
import { isTransientError } from '../../utilities/errors/isTransientError';
import { getLogger, LogLevel, LogCategory, LogEvent } from '../logger';

type SuccessResponse<ResponseData> = ResponseData;
export type QueryFunctionType<QueryData, QueryArgs> = (
  args: QueryArgs,
) => UseQueryResult<QueryData>;
export type ExtractQueryArgs<T> =
  T extends QueryFunctionType<unknown, infer Args> ? Args : never;
export type ExtractQueryResult<T> =
  T extends QueryFunctionType<infer Data, unknown> ? Data : never;

/**
 * @deprecated Use hasErrorResponse from @cntxt/shared/utilities/errors instead
 * Kept for backward compatibility
 */
export function isErrorResponse<T>(
  value: ErrorResponse | T | null | undefined,
): value is ErrorResponse {
  return hasErrorResponse(value);
}

export function isSuccessResponse<DataType>(
  data: ErrorResponse | SuccessResponse<DataType> | null | undefined,
): data is Exclude<NonNullable<SuccessResponse<DataType>>, ErrorResponse> {
  if (data === null || data === undefined) {
    return false;
  }
  return (
    (typeof data === 'object' && !('error' in data)) ||
    typeof data === 'string' ||
    typeof data === 'number' ||
    typeof data === 'boolean'
  );
}

// this does not work, but would be cool if it did!
// export type EnsuredResult<
//   Query extends (...args: any) => ErrorResponse | SuccessResponse<unknown>
// > = Exclude<NonNullable<ReturnType<Query>>, ErrorResponse>;

const logger = getLogger('data-fetching');

function logAndThrowDataFetchError(error: HttpError): never {
  const diagnostics = getErrorDiagnostics(error);

  logger.contextualLog(
    LogLevel.ERROR,
    `[EnsureData] ${error.message}`,
    undefined,
    {
      category: LogCategory.API,
      event: LogEvent.API_ERROR,
      error,
      ...(isDataFetchError(error)
        ? { dataFetchDiagnostics: error.diagnostics }
        : {}),
    },
  );

  logErrorDiagnosticsToConsole('[EnsureData]', error, diagnostics);

  throw error;
}

type QuerySnapshot = {
  fetchStatus: string;
  status: string;
  isFetching: boolean;
  isLoading: boolean;
};

/**
 * React Query can report `isLoading: false` while a fetch is still in flight or
 * paused (e.g. offline / query-key transition). Treat those as loading so we do
 * not throw "Data not found" prematurely.
 */
export function isQueryPendingWithoutData(
  snapshot: QuerySnapshot,
  hasNoData: boolean,
  hasError: boolean,
): boolean {
  if (hasError || !hasNoData) {
    return false;
  }

  return (
    snapshot.isLoading ||
    snapshot.isFetching ||
    snapshot.fetchStatus === 'fetching' ||
    snapshot.fetchStatus === 'paused' ||
    snapshot.status === 'pending'
  );
}

function handleQueryRetry(refetch: () => Promise<unknown>): void {
  refetch().catch(() => undefined);
}

/**
 * Names the state of a query that is holding a spinner.
 *
 * Shared so that the other places which hold a bare `<Loading/>` off a query —
 * `AuthenticatedWithRoles` most of all, which gates every module page on
 * `useGetAccessRolesQuery` without going through `EnsureData` — report a stall
 * in the same words rather than staying silent.
 *
 * Every state is named, `fetching` included. That one used to be skipped on the
 * reasoning that a slow request shows up in a network log, which holds only
 * while a request is actually on the wire — a query whose promise never settles
 * reports `fetching` with nothing in flight, and a trace records responses, not
 * attempts. Pair this with the runner's in-flight request list to tell a slow
 * fetch from a stuck one.
 */
export function describeIdleSpinner(
  fetchStatus: string,
  status: string,
): string | null {
  if (fetchStatus === 'paused') {
    return 'fetch paused — onlineManager reports offline';
  }
  if (fetchStatus === 'idle') {
    return `no fetch in flight (status=${status})`;
  }
  // `fetching` used to return null here, on the reasoning that a slow request
  // is visible in any network log. That is only true while a request is
  // actually on the wire: a query whose promise never settles reports
  // `fetching` with nothing in flight, and a trace records only responses, so
  // the one state that most needed reporting was the one being skipped.
  return `fetching for longer than expected (status=${status})`;
}

/** How long a query may hold the spinner before it is worth reporting. */
const STUCK_QUERY_WARNING_MS = 15_000;

/**
 * Reports a query that has held the loading state long enough that it is
 * almost certainly not coming back.
 *
 * A never-enabled query, a paused fetch and a cancelled request all render
 * `<Loading/>` forever and look identical from outside: a spinner, no traffic,
 * no error. A trace cannot separate them either, since it records only requests
 * that were answered. Naming the state is the only way to know which.
 *
 * `onlineManager` is included because it decides between those branches and
 * keeps its own copy of online-ness, so it can disagree with the browser.
 */
export function useStuckQueryWarning(
  reason: string | null,
  queryIdentifier: string,
): void {
  React.useEffect(() => {
    if (!reason) {
      return;
    }
    const timer = setTimeout(() => {
      logger.contextualLog(
        LogLevel.WARN,
        `[EnsureData] ${queryIdentifier} still loading after ${STUCK_QUERY_WARNING_MS}ms — ${reason}, onlineManager=${onlineManager.isOnline()}, navigator.onLine=${typeof navigator === 'undefined' ? 'n/a' : navigator.onLine}`,
        undefined,
        { category: LogCategory.API, event: LogEvent.API_REQUEST },
      );
    }, STUCK_QUERY_WARNING_MS);
    return () => clearTimeout(timer);
  }, [reason, queryIdentifier]);
}

function renderTransientNetworkFallback(
  error: unknown,
  queryIdentifier: string,
  isFetching: boolean,
  refetch: () => Promise<unknown>,
): React.ReactElement {
  logger.contextualLog(
    LogLevel.WARN,
    `[EnsureData] transient network failure for ${queryIdentifier}`,
    undefined,
    {
      category: LogCategory.API,
      event: LogEvent.API_ERROR,
      error: error instanceof Error ? error : new Error(String(error)),
    },
  );

  return (
    <ConnectionErrorFallback
      error={error}
      isRetrying={isFetching}
      onRetry={() => handleQueryRetry(refetch)}
    />
  );
}

function throwResolvedQueryError(
  error: unknown,
  diagnosticBase: Parameters<typeof toQueryHttpError>[1],
): never {
  if (error instanceof HttpError) {
    logAndThrowDataFetchError(error);
  }

  logAndThrowDataFetchError(toQueryHttpError(error, diagnosticBase));
}

/**
 * @param debug - set to true to log errors to console
 * @param loadingComponent - component to show while loading
 * @param noLoader - set to true to disable the loading component and show content immediately
 * @param noContentWhileLoading - set to true to show nothing while loading
 * @param queryName - optional name to identify the query in error messages
 */
export function EnsureData<
  QueryData = ErrorResponse | SuccessResponse<unknown>,
  QueryArgs = unknown,
>({
  query,
  queryArgs = {} as QueryArgs,
  children,
  noLoader,
  noContentWhileLoading,
  debug,
  loadingComponent,
  queryName,
}: Readonly<{
  query: QueryFunctionType<QueryData, QueryArgs>;
  queryArgs?: QueryArgs;
  noLoader?: boolean;
  noContentWhileLoading?: boolean;
  debug?: boolean;
  loadingComponent?: React.ReactNode;
  queryName?: string;
  children: (
    props: {
      data: Exclude<NonNullable<QueryData>, ErrorResponse>;
      isLoading?: boolean;
    } & Omit<
      QueryObserverBaseResult<QueryData>,
      'data' | 'error' | 'isLoading'
    >,
  ) => React.ReactElement<unknown> | null;
}>) {
  const queryResult = query(queryArgs);
  const {
    data,
    isLoading,
    error,
    fetchStatus,
    status,
    isFetching,
    refetch,
    ...rest
  } = queryResult;

  const queryObserverRest = {
    fetchStatus,
    status,
    isFetching,
    refetch,
    ...rest,
  };

  const resolvedQueryName = queryName || query.name || 'unknown query';

  const querySnapshot: QuerySnapshot = {
    fetchStatus,
    status,
    isFetching,
    isLoading,
  };

  const diagnosticBase = {
    queryName: resolvedQueryName,
    queryArgs,
    fetchStatus,
    queryStatus: status,
    isFetching,
    isLoading,
  };

  // Generate query identifier for error messages
  const queryIdentifier = React.useMemo(() => {
    const argsString =
      queryArgs && Object.keys(queryArgs as object).length > 0
        ? ` with args: ${JSON.stringify(queryArgs)}`
        : '';
    return `[${resolvedQueryName}]${argsString}`;
  }, [resolvedQueryName, queryArgs]);

  const hasNoData = data === undefined || data === null;
  const hasQueryError = error !== undefined && error !== null;

  // Disabled queries stay pending with an idle fetch status and never receive data.
  const isDisabled =
    !isLoading &&
    !error &&
    hasNoData &&
    fetchStatus === 'idle' &&
    status === 'pending';

  const isPending = isQueryPendingWithoutData(
    querySnapshot,
    hasNoData,
    hasQueryError,
  );

  // The question worth asking is not which internal state we are in, but
  // whether this component is holding the screen with nothing in flight behind
  // it. `fetching` is the one state still making progress, and it is already
  // visible in any network log, so it is the only one left out.
  const holdsSpinner = !noLoader && !noContentWhileLoading;
  let stuckReason: string | null = null;
  if (hasQueryError && isCancellationError(error)) {
    // No `holdsSpinner` guard: this branch renders `<Loading/>` regardless.
    stuckReason = 'request cancelled';
  } else if (holdsSpinner && (isDisabled || isLoading || isPending)) {
    // A disabled query is a normal state in itself, but a normal state has no
    // business holding a spinner this long: whatever was meant to enable it
    // never did.
    stuckReason = isDisabled
      ? 'query disabled — nothing ever enabled it to run'
      : describeIdleSpinner(fetchStatus, status);
  }
  useStuckQueryWarning(stuckReason, queryIdentifier);

  if (isDisabled) {
    if (debug) {
      logger.contextualLog(
        LogLevel.DEBUG,
        `Query ${queryIdentifier} is disabled, returning null`,
        undefined,
        { category: LogCategory.API, event: LogEvent.API_REQUEST },
      );
    }
    if (noContentWhileLoading) {
      return null;
    }
    if (noLoader) {
      return children({
        data: {} as Exclude<NonNullable<QueryData>, ErrorResponse>,
        isLoading: false,
        ...queryObserverRest,
      });
    }
    return loadingComponent ?? <Loading source="EnsureData:disabled" />;
  }

  if (isLoading || isPending) {
    if (noContentWhileLoading) {
      return null;
    }
    if (noLoader) {
      return children({
        data: {} as Exclude<NonNullable<QueryData>, ErrorResponse>,
        isLoading: isLoading || isPending,
        ...queryObserverRest,
      });
    }

    return loadingComponent ?? <Loading source="EnsureData:pending" />;
  }

  if (error) {
    // Navigation or query teardown — keep showing loader instead of error UI.
    if (isCancellationError(error)) {
      return loadingComponent ?? <Loading source="EnsureData:cancelled" />;
    }

    if (isTransientError(error)) {
      return renderTransientNetworkFallback(
        error,
        queryIdentifier,
        isFetching,
        refetch,
      );
    }

    throwResolvedQueryError(error, diagnosticBase);
  }

  if (hasNoData) {
    logAndThrowDataFetchError(
      createDataFetchError({
        ...diagnosticBase,
        reason: 'missing-data',
      }),
    );
  }

  if (hasErrorResponse(data)) {
    logAndThrowDataFetchError(
      createDataFetchError(
        {
          ...diagnosticBase,
          reason: 'error-response',
        },
        {
          cause: new Error(JSON.stringify(data.error)),
        },
      ),
    );
  }

  if (isSuccessResponse<QueryData>(data)) {
    if (debug) {
      logger.contextualLog(
        LogLevel.DEBUG,
        `Query ${queryIdentifier} success response data:`,
        undefined,
        {
          category: LogCategory.API,
          event: LogEvent.API_RESPONSE,
        },
      );
    }
    return children({
      data: data,
      ...queryObserverRest,
    });
  }

  logAndThrowDataFetchError(
    createDataFetchError({
      ...diagnosticBase,
      reason: 'unexpected-format',
    }),
  );
}
